import {
  collection,
  doc,
  getDocs,
  getDoc,
  setDoc,
  deleteDoc,
  updateDoc,
  query,
  where,
  onSnapshot,
  serverTimestamp,
  deleteField,
} from 'firebase/firestore';
import { db } from './firebase';
import { WatchlistItem } from './store';
import { UserProfile } from '@/types/profile';
import { tmdb } from './tmdb';

const WATCHLIST_COLLECTION = 'watchlist';

export class FirestoreService {
  static async saveWatchlistItem(userId: string, item: WatchlistItem): Promise<void> {
    try {
      const docRef = doc(db, `users/${userId}/${WATCHLIST_COLLECTION}/${item.id}`);

      // Filter out undefined values to prevent Firestore errors
      const cleanItem = Object.fromEntries(
        Object.entries(item).filter(([_, value]) => value !== undefined)
      );

      await setDoc(docRef, {
        ...cleanItem,
        updatedAt: serverTimestamp(),
      });
    } catch (error) {
      throw error;
    }
  }

  static async removeWatchlistItem(userId: string, itemId: number): Promise<void> {
    try {
      const docRef = doc(db, `users/${userId}/${WATCHLIST_COLLECTION}/${itemId}`);
      await deleteDoc(docRef);
    } catch (error) {
      throw error;
    }
  }

  static async updateWatchlistItem(
    userId: string,
    itemId: number,
    updates: Partial<WatchlistItem>
  ): Promise<void> {
    try {
      const docRef = doc(db, `users/${userId}/${WATCHLIST_COLLECTION}/${itemId}`);

      // Process updates: filter undefined, convert null to deleteField()
      const cleanUpdates: Record<string, any> = {};
      Object.entries(updates).forEach(([key, value]) => {
        if (value === undefined) {
          // Skip undefined values
          return;
        } else if (value === null) {
          // Use deleteField() for null values to remove the field from Firestore
          cleanUpdates[key] = deleteField();
        } else {
          // Keep all other values
          cleanUpdates[key] = value;
        }
      });

      await updateDoc(docRef, {
        ...cleanUpdates,
        updatedAt: serverTimestamp(),
      });
    } catch (error) {
      throw error;
    }
  }

  static async getWatchlist(userId: string): Promise<WatchlistItem[]> {
    try {
      const collectionRef = collection(db, `users/${userId}/${WATCHLIST_COLLECTION}`);
      const snapshot = await getDocs(collectionRef);

      return snapshot.docs.map(doc => {
        const data = doc.data();
        return {
          ...data,
          id: Number(doc.id),
        } as WatchlistItem;
      });
    } catch (error) {
      throw error;
    }
  }

  static subscribeToWatchlist(
    userId: string,
    callback: (items: WatchlistItem[]) => void
  ): () => void {
    const collectionRef = collection(db, `users/${userId}/${WATCHLIST_COLLECTION}`);

    return onSnapshot(collectionRef, (snapshot) => {
      const items = snapshot.docs.map(doc => {
        const data = doc.data();
        return {
          ...data,
          id: Number(doc.id),
        } as WatchlistItem;
      });
      callback(items);
    });
  }

  static async markSeasonWatched(
    userId: string,
    showId: number,
    seasonNumber: number
  ): Promise<void> {
    try {
      const docRef = doc(db, `users/${userId}/${WATCHLIST_COLLECTION}/${showId}`);

      // First get the current item to update the watchedSeasons array
      const item = await this.getWatchlistItem(userId, showId);
      if (!item) throw new Error('Watchlist item not found');

      // Auto-mark all previous seasons as watched
      const seasonsToMark = [];
      for (let i = 1; i <= seasonNumber; i++) {
        seasonsToMark.push(i);
      }

      const updatedWatchedSeasons = [...new Set([...item.watchedSeasons, ...seasonsToMark])];

      // Fetch episode data for each season and build watchedEpisodes
      const watchedEpisodes: Record<number, number[]> = item.watchedEpisodes || {};
      const episodeWatchDates: Record<string, string> = item.episodeWatchDates || {};
      const now = new Date().toISOString();

      for (const season of seasonsToMark) {
        try {
          // Fetch season details from TMDB
          const seasonData = await tmdb.getSeason(showId, season);
          const episodeCount = seasonData.episodes?.length || 0;

          if (episodeCount > 0) {
            // Mark all episodes as watched
            const episodeNumbers = Array.from({ length: episodeCount }, (_, i) => i + 1);
            watchedEpisodes[season] = episodeNumbers;

            // Record watch dates for all episodes
            episodeNumbers.forEach(epNum => {
              episodeWatchDates[`S${season}E${epNum}`] = now;
            });
          }
        } catch (error) {
          // Continue with season marking even if episode fetch fails
          console.error(`Failed to fetch episodes for season ${season}:`, error);
        }
      }

      // Check if this clears the new content flag
      const shouldClearNewContent = item.newSeasonNumber
        ? seasonNumber >= item.newSeasonNumber
        : true;

      const updates: any = {
        watchedSeasons: updatedWatchedSeasons,
        watchedEpisodes,
        episodeWatchDates,
        lastWatched: now,
        updatedAt: serverTimestamp(),
      };

      if (shouldClearNewContent) {
        updates.hasNewContent = false;
        updates.newSeasonNumber = null;
        updates.nextEpisode = null;
      }

      await updateDoc(docRef, updates);
    } catch (error) {
      throw error;
    }
  }

  static async markSeasonUnwatched(
    userId: string,
    showId: number,
    seasonNumber: number
  ): Promise<void> {
    try {
      const docRef = doc(db, `users/${userId}/${WATCHLIST_COLLECTION}/${showId}`);

      const item = await this.getWatchlistItem(userId, showId);
      if (!item) throw new Error('Watchlist item not found');

      const updatedWatchedSeasons = item.watchedSeasons.filter(s => s !== seasonNumber);

      // Remove episode data for this season
      const updatedWatchedEpisodes = item.watchedEpisodes
        ? Object.fromEntries(
            Object.entries(item.watchedEpisodes).filter(([s]) => parseInt(s) !== seasonNumber)
          )
        : {};

      // Remove episode watch dates for this season
      const updatedEpisodeWatchDates = item.episodeWatchDates
        ? Object.fromEntries(
            Object.entries(item.episodeWatchDates).filter(([key]) => !key.startsWith(`S${seasonNumber}E`))
          )
        : {};

      await updateDoc(docRef, {
        watchedSeasons: updatedWatchedSeasons,
        watchedEpisodes: updatedWatchedEpisodes,
        episodeWatchDates: updatedEpisodeWatchDates,
        updatedAt: serverTimestamp(),
      });
    } catch (error) {
      throw error;
    }
  }

  static async markEpisodeWatched(
    userId: string,
    showId: number,
    seasonNumber: number,
    episodeNumber: number
  ): Promise<void> {
    try {
      const watchlistRef = collection(db, 'users', userId, 'watchlist');
      const q = query(watchlistRef, where('id', '==', showId), where('type', '==', 'tv'));
      const snapshot = await getDocs(q);

      if (!snapshot.empty) {
        const docRef = doc(db, 'users', userId, 'watchlist', snapshot.docs[0].id);
        const currentData = snapshot.docs[0].data();

        // Get current episode data
        const watchedEpisodes = currentData.watchedEpisodes || {};
        const seasonEpisodes = watchedEpisodes[seasonNumber] || [];
        const episodeWatchDates = currentData.episodeWatchDates || {};

        // Add episode if not already there
        if (!seasonEpisodes.includes(episodeNumber)) {
          const newSeasonEpisodes = [...seasonEpisodes, episodeNumber].sort((a, b) => a - b);
          watchedEpisodes[seasonNumber] = newSeasonEpisodes;

          // Record watch date
          const episodeKey = `S${seasonNumber}E${episodeNumber}`;
          episodeWatchDates[episodeKey] = new Date().toISOString();

          await updateDoc(docRef, {
            watchedEpisodes,
            episodeWatchDates,
            lastWatched: new Date().toISOString(),
          });
        }
      }
    } catch (error) {
      throw error;
    }
  }

  static async markEpisodeUnwatched(
    userId: string,
    showId: number,
    seasonNumber: number,
    episodeNumber: number
  ): Promise<void> {
    try {
      const watchlistRef = collection(db, 'users', userId, 'watchlist');
      const q = query(watchlistRef, where('id', '==', showId), where('type', '==', 'tv'));
      const snapshot = await getDocs(q);

      if (!snapshot.empty) {
        const docRef = doc(db, 'users', userId, 'watchlist', snapshot.docs[0].id);
        const currentData = snapshot.docs[0].data();

        // Get current episode data
        const watchedEpisodes = currentData.watchedEpisodes || {};
        const seasonEpisodes = watchedEpisodes[seasonNumber] || [];
        const episodeWatchDates = currentData.episodeWatchDates || {};
        const watchedSeasons = currentData.watchedSeasons || [];

        // Remove episode
        const newSeasonEpisodes = seasonEpisodes.filter((ep: number) => ep !== episodeNumber);
        watchedEpisodes[seasonNumber] = newSeasonEpisodes;

        // Remove watch date
        const episodeKey = `S${seasonNumber}E${episodeNumber}`;
        delete episodeWatchDates[episodeKey];

        // Remove season from watchedSeasons if it was there
        const newWatchedSeasons = watchedSeasons.filter((s: number) => s !== seasonNumber);

        await updateDoc(docRef, {
          watchedEpisodes,
          episodeWatchDates,
          watchedSeasons: newWatchedSeasons,
        });
      }
    } catch (error) {
      throw error;
    }
  }

  private static async getWatchlistItem(userId: string, itemId: number): Promise<WatchlistItem | null> {
    try {
      const watchlist = await this.getWatchlist(userId);
      return watchlist.find(item => item.id === itemId) || null;
    } catch (error) {
      return null;
    }
  }

  static async saveUserPreferences(
    userId: string,
    preferences: {
      notifications: boolean;
      checkFrequency: 'daily' | 'weekly';
      emailNotifications: boolean;
    }
  ): Promise<void> {
    try {
      const docRef = doc(db, `users/${userId}/settings/preferences`);
      await setDoc(docRef, {
        ...preferences,
        updatedAt: serverTimestamp(),
      });
    } catch (error) {
      throw error;
    }
  }

  /**
   * Get a user profile by username
   * Searches the users collection for a document with matching username
   */
  static async getUserByUsername(username: string): Promise<{ uid: string; profile: UserProfile } | null> {
    try {
      const usersRef = collection(db, 'users');
      const q = query(usersRef, where('username', '==', username));
      const snapshot = await getDocs(q);

      if (snapshot.empty) {
        return null;
      }

      const userDoc = snapshot.docs[0];
      return {
        uid: userDoc.id,
        profile: userDoc.data() as UserProfile,
      };
    } catch (error) {
      throw error;
    }
  }

  /**
   * Get a user profile by user ID
   */
  static async getUserProfile(userId: string): Promise<UserProfile | null> {
    try {
      const docRef = doc(db, `users/${userId}`);
      const docSnap = await getDoc(docRef);

      if (!docSnap.exists()) {
        return null;
      }

      return docSnap.data() as UserProfile;
    } catch (error) {
      throw error;
    }
  }
}